import { useState, useEffect, useRef } from 'react';

export default function CultureSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const culturePillars = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation & Curiosity',
      description: 'Always exploring new technologies and unconventional ideas.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaborative Spirit',
      description: "Working together, supporting each other's growth and success.",
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: 'ri-book-read-line',
      title: 'Continuous Learning',
      description: 'Embracing new skills and staying ahead in a dynamic industry.',
      color: 'from-pink-500 to-purple-500'
    },
    {
      icon: 'ri-focus-2-line',
      title: 'Impact-Driven',
      description: 'Creating meaningful results for our clients and the digital world.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: 'ri-map-pin-user-line',
      title: 'Vibrant Kochi Life',
      description: 'Enjoying the rich culture and balanced lifestyle our city offers.',
      color: 'from-blue-500 to-teal-500'
    }
  ];

  return (
    <section id="culture" ref={sectionRef} className="py-16 md:py-24 bg-gradient-to-b from-gray-900 to-black relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Our Culture:
            </span>
            <span className="block text-white mt-2">Where Innovation Thrives</span>
          </h2>
          <h3 className="text-xl md:text-3xl text-orange-400 mb-6 font-semibold">
            More Than Just a Job. It's a Calling.
          </h3>
          <p className="text-lg text-gray-300 max-w-5xl mx-auto leading-relaxed">
            We believe a truly innovative company is built on a foundation of creativity, continuous learning, 
            and mutual respect. Our culture at Hash Adz fosters an environment where ideas are celebrated, 
            challenges are embraced, and every team member has the opportunity to grow.
          </p>
        </div>

        <div className={`mb-16 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="relative group rounded-3xl overflow-hidden">
            <img
              src="/assets/culture-team.jpg"
              alt="Hash Adz Team Culture"
              className="w-full h-64 md:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {culturePillars.map((pillar, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-orange-500/30 h-full">
                <div className={`w-14 h-14 bg-gradient-to-r ${pillar.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${pillar.icon} text-2xl text-white`}></i>
                </div>
                
                <h4 className="text-xl font-bold text-white mb-3">
                  {pillar.title}
                </h4>
                
                <p className="text-gray-400 leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}